import React, { useState, useEffect } from 'react';
import { Card, Typography, Statistic, Table, Tag, Spin, Alert, Row, Col, Divider } from 'antd';
import { UserOutlined, WalletOutlined, HistoryOutlined } from '@ant-design/icons';
import { userApi } from '../../api/apiService';
import { useAuth } from '../../contexts/AuthContext';

const { Title } = Typography;

const UserProfile = () => {
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      
      // Fetch user profile
      const profileResponse = await userApi.getProfile();
      setProfile(profileResponse.user);
      
      // Fetch transaction history
      const transactionsResponse = await userApi.getTransactions();
      setTransactions(transactionsResponse.transactions || []);
      
      setError(null);
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setError('获取用户资料失败，请稍后再试');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: '日期',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp) => new Date(timestamp).toLocaleString('zh-CN')
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        let color = 'blue';
        let text = type;
        
        if (type === 'subscription') {
          color = 'red';
          text = '订阅';
        } else if (type === 'topup') {
          color = 'green';
          text = '充值';
        }
        
        return <Tag color={color}>{text}</Tag>;
      }
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => {
        const isNegative = amount < 0;
        return (
          <span style={{ color: isNegative ? '#ff4d4f' : '#52c41a' }}>
            {isNegative ? '-' : '+'} ¥{Math.abs(amount).toFixed(2)}
          </span>
        );
      }
    },
    {
      title: '余额',
      dataIndex: 'balance',
      key: 'balance',
      render: (balance) => `$${balance.toFixed(2)}`
    }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '20px' }}>加载中...</div>
      </div>
    );
  }

  if (error) {
    return <Alert message={error} type="error" />;
  }

  return (
    <div>
      <Title level={2}>用户资料</Title>
      
      <Row gutter={16}>
        <Col span={8}>
          <Card>
            <Statistic
              title="用户名"
              value={currentUser?.username || profile?.username || '未设置'}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="账户余额"
              value={profile?.balance || 0}
              precision={2}
              prefix={<WalletOutlined />}
              suffix="$"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="费率"
              value={profile?.rate || 1.0}
              precision={2}
              suffix="$/号码"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      <Divider orientation="left">
        <HistoryOutlined /> 交易记录
      </Divider>

      {transactions.length > 0 ? (
        <Table 
          dataSource={transactions} 
          columns={columns} 
          rowKey={(record) => `${record.timestamp}-${record.description}`}
          pagination={{ pageSize: 10 }}
        />
      ) : (
        <Alert 
          message="暂无交易记录" 
          type="info" 
          showIcon 
        />
      )}
    </div>
  );
};

export default UserProfile; 